import { useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { AvatarStack, type AvatarUser } from "@/components/ui/Avatar";
import { Icon } from "@/components/ui/Icon";
import { ActionDialog } from "@/components/board/ActionDialog";
import { TicketAgeChip, TicketIdChip } from "@/components/board/TicketChips";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormat } from "@/hooks/useDateFormat";
import { useRowAction } from "@/hooks/useRowAction";
import { useWorkspaceMembers } from "@/hooks/useWorkspaceMembers";
import { useBoardPeople } from "@/hooks/useBoardPeople";
import { boardPeopleById } from "@/lib/boardPeople";
import { deriveAvatarColor, deriveInitials } from "@/lib/colors";
import { relDate } from "@/lib/format";
import { BRAND, statusColorOf } from "@/lib/tokens";
import { showToast } from "@/lib/toast";
import { roleOf } from "@/lib/statusRoles";
import { defaultActionRecipients } from "@/lib/actionRecipients";
import { t } from "@/lib/strings";
import type { CellValue, RowActionType, StatusRole, UUID } from "@/lib/types";
import { isWorkflowBoard, ticketLabel, type ViewBoard, type ViewRow } from "@/lib/boardView";

// Sentinel lane for rows whose status cell is empty or references a deleted
// option. Cards can be dragged OUT of it but never INTO it.
const NO_STATUS_LANE = "__no_status__";

// Cursor-first collision detection. rectIntersection alone picks the lane
// with the biggest CARD overlap — when dragging backwards (left), the card
// body trails over the origin lane and wins, so the drop silently no-ops.
// The lane under the pointer is what the user means; rect overlap is only
// the fallback (e.g. keyboard-driven drags with no pointer coordinates).
const laneUnderCursor: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  return pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args);
};

interface Lane {
  id: string;
  label: string;
  color: string;
  role: StatusRole | null;
}

interface KanbanViewProps {
  board: ViewBoard;
  onRowClick: (row: ViewRow) => void;
  onPatchRow: (id: string, patch: Partial<ViewRow>) => void;
  /** Which status column the lanes bucket by. Defaults to the first status
   * column; the board's Kanban-group picker overrides it. */
  statusColumnId?: string | null;
  /** Project-board drop writes the chosen status column's cell directly (the
   * field-based onPatchRow only targets the FIRST status column). */
  onPatchCell?: (rowId: string, columnId: string, value: CellValue) => void;
  /** Row whose detail panel is open — its card gets a persistent brand ring. */
  openRowId?: string | null;
}

// A row's value for a specific status column (read from raw cells, since
// ViewRow.status is hardwired to the first status column).
function statusValueOf(row: ViewRow, columnId: string | undefined): string | null {
  if (!columnId) return null;
  const v = row.raw?.cells?.find((c) => c.column_def_id === columnId)?.value;
  return typeof v === "string" ? v : null;
}

// Maps a drop-target lane's semantic role to the Phase 10 action that legally
// moves a ticket into that status. Mirrors backend services/actions.py
// role-targeting (with the same label fallback via roleOf()).
function actionForTargetRole(
  role: StatusRole | null,
  viewerIsCreator: boolean,
): RowActionType | null {
  if (role === "in_progress") return "annehmen";
  if (role === "waiting") return "wartet_auf_kunde";
  if (role === "closed") return "loesen";
  if (role === "open") return viewerIsCreator ? "erneut_oeffnen" : null;
  return null;
}

// Maps a specific drop-target OPTION to the action that moves a ticket there.
// Several options now share a role (open → offen/gesichtet/eingeplant), so a
// role-only mapping mis-routes a drop (the In Review lane would fire
// "annehmen"). Stable option ids are matched first; unknown/custom ids fall
// back to the role mapping so non-IT boards still work.
function actionForTargetOption(
  optionId: string,
  role: StatusRole | null,
  viewerIsCreator: boolean,
): RowActionType | null {
  switch (optionId) {
    case "offen":
      return viewerIsCreator ? "erneut_oeffnen" : null;
    case "gesichtet":
      return "sichten";
    case "eingeplant":
      return "einplanen";
    case "in-bearbeitung":
      return "annehmen";
    case "in-review":
      return "in_review";
    case "wartet":
      return "wartet_auf_kunde";
    case "zurueckgestellt":
      return "zurueckstellen";
  }
  if (role === "closed") return "loesen";
  return actionForTargetRole(role, viewerIsCreator);
}

export function KanbanView({
  board,
  onRowClick,
  onPatchRow,
  onPatchCell,
  statusColumnId,
  openRowId = null,
}: KanbanViewProps) {
  const { user } = useAuth();
  const isTickets = isWorkflowBoard(board);
  // Ideas boards render grouped by status but the drag→action mapper only knows
  // TICKET verbs (dragging an idea would fire loesen/annehmen and force a
  // Gutachter assignment — spec §6: status and assignment are independent). So
  // on ideas boards drag-across-status is disabled entirely; the RowActionBar
  // (Annehmen/Ablehnen/Zuweisen) is the single decision path. isTickets stays
  // true for ideas so the ticket-label visual etc. are unchanged.
  const isIdeas = board.kind === "ideas";
  const statusCol =
    (statusColumnId
      ? board.columns.find((c) => c.id === statusColumnId && c.type === "status")
      : undefined) ?? board.columns.find((c) => c.type === "status");
  const statusColId = statusCol?.id;
  const members = useWorkspaceMembers(board.workspace_id as UUID);

  const [activeRow, setActiveRow] = useState<ViewRow | null>(null);
  const [pendingDrop, setPendingDrop] = useState<{
    row: ViewRow;
    action: RowActionType;
    // The exact lane dropped on — pre-selects the resolve outcome when a board
    // has several closed options (Gelöst vs. Nicht relevant).
    targetOptionId?: string;
  } | null>(null);
  // The browser fires a synthetic click on the dragged card after every
  // completed drag (pointer capture retargets pointerup to the source).
  // Without suppression, every drop ALSO opens the row detail panel, which
  // fights the ActionDialog (Radix dismisses on outside interaction).
  const justDragged = useRef(false);
  // Last lane the pointer hovered during the drag. dnd-kit's `event.over` can
  // come back null on drop when the lane container auto-scrolls (the rect the
  // collision was computed against shifts between the last move and pointerup)
  // — this was the "drag only works on the second try" bug. Tracking it via
  // onDragOver makes the drop target resilient to that race.
  const overLaneRef = useRef<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const rowAction = useRowAction(pendingDrop?.row.raw?.id as UUID | undefined, board.id as UUID);

  const otherMembers = useMemo(
    () => (members.data ?? []).filter((m) => !user?.email || m.email !== user.email),
    [members.data, user?.email],
  );
  // Notify-recipient name pool incl. cross-dept guests (e.g. the ticket
  // creator) — members-only would render them as a "—" chip.
  const boardPeople = useBoardPeople(board.id as UUID);
  const recipientPool = useMemo(
    () => (boardPeople.data ?? []).filter((p) => !user?.email || p.email !== user.email),
    [boardPeople.data, user?.email],
  );

  // 🔴 Resolves from board PEOPLE (members ∪ guests), not workspace members. A row is routinely
  // assigned to a cross-department colleague who holds nothing but a row-scoped guest grant —
  // on a ticket board that is the normal case, not an edge one. Against members-only they came
  // back undefined and the card drew an em-dash "—" avatar, while the TABLE view of the same
  // board (PersonCell → useBoardPeople) drew them correctly. Two views of one board disagreeing
  // about who exists. The right pool was already imported in this file for `recipientPool`
  // below, with a comment saying exactly this — it just never reached the card.
  const peopleById = useMemo(
    () => boardPeopleById(members.data, boardPeople.data),
    [members.data, boardPeople.data],
  );
  // Annehmen-on-drop: owners/admins assign anyone in the department, members
  // only themselves.
  const me = useMemo(
    () => (members.data ?? []).find((m) => Boolean(user?.email) && m.email === user?.email),
    [members.data, user?.email],
  );
  const assigneeOptions =
    me?.role === "owner" || me?.role === "admin" ? (members.data ?? []) : me ? [me] : [];

  // Kanban shows top-level rows only (sub-items are checklists, not tickets).
  const topRows = useMemo(() => board.rows.filter((r) => !r.parentRowId), [board.rows]);

  const lanes: Lane[] = useMemo(() => {
    const opts: Lane[] = (statusCol?.options ?? []).map((o) => ({
      id: o.id,
      label: o.label,
      color: o.color as string,
      role: roleOf(o),
    }));
    const known = new Set(opts.map((o) => o.id));
    const hasOrphans = topRows.some((r) => {
      const s = statusValueOf(r, statusColId);
      return !s || !known.has(s);
    });
    return hasOrphans
      ? [{ id: NO_STATUS_LANE, label: t.kanbanNoStatusLane, color: "gray", role: null }, ...opts]
      : opts;
  }, [statusCol, statusColId, topRows]);

  // Closed outcomes offered when resolving (e.g. Gelöst / Nicht relevant).
  const closedOutcomes = useMemo(
    () =>
      lanes
        .filter((l) => l.role === "closed")
        .map((l) => ({ id: l.id, label: l.label, color: l.color })),
    [lanes],
  );

  const rowsByLane = useMemo(() => {
    const map = new Map<string, ViewRow[]>();
    for (const lane of lanes) map.set(lane.id, []);
    const known = new Set(lanes.map((l) => l.id));
    for (const r of topRows) {
      const s = statusValueOf(r, statusColId);
      const laneId = s && known.has(s) ? s : NO_STATUS_LANE;
      map.get(laneId)?.push(r);
    }
    return map;
  }, [lanes, statusColId, topRows]);

  function viewerIsCreatorOf(row: ViewRow): boolean {
    return (
      Boolean(user?.sub) &&
      row.raw?.created_by_keycloak_sub != null &&
      user?.sub === row.raw.created_by_keycloak_sub
    );
  }

  function canDropOn(row: ViewRow, lane: Lane): boolean {
    if (isIdeas) return false; // ideas: no drag-to-status — action bar is the decision path
    if (lane.id === NO_STATUS_LANE) return false;
    if (lane.id === statusValueOf(row, statusColId)) return false;
    if (!isTickets) return true;
    if (!row.raw) return false; // fixture mode — no real backend row to act on
    return actionForTargetOption(lane.id, lane.role, viewerIsCreatorOf(row)) !== null;
  }

  function avatarUsersFor(row: ViewRow): AvatarUser[] {
    return row.owners.map(
      (id) =>
        peopleById.get(id as UUID) ?? {
          id,
          display_name: "—",
          initials: deriveInitials("—"),
          avatar_color: deriveAvatarColor(id),
          username: null,
          department_workspace_id: null,
          department_color: null,
        },
    );
  }

  function defaultRecipientsFor(row: ViewRow, action: RowActionType): UUID[] {
    const raw = row.raw;
    if (!raw) return [];
    // Same pure decision as RowActionBar — Annehmen / Senden zur Prüfung / Lösen
    // pre-select the ticket creator (both surfaces must agree).
    const meId =
      (members.data ?? []).find((m) => Boolean(user?.email) && m.email === user?.email)?.user_id ??
      null;
    return defaultActionRecipients(action, {
      createdBy: (raw.created_by as UUID | null) ?? null,
      lastActorUserId: (raw.last_actor_user_id as UUID | null) ?? null,
      ownerIds: (members.data ?? []).filter((m) => m.role === "owner").map((m) => m.user_id),
      otherMemberIds: otherMembers.map((m) => m.user_id),
      meId,
    });
  }

  function handleCardClick(row: ViewRow) {
    if (justDragged.current) return;
    onRowClick(row);
  }

  function handleDragStart(event: DragStartEvent) {
    justDragged.current = true;
    overLaneRef.current = null;
    const row = topRows.find((r) => r.id === event.active.id);
    setActiveRow(row ?? null);
  }

  function handleDragOver(event: DragOverEvent) {
    overLaneRef.current = (event.over?.id as string | undefined) ?? overLaneRef.current;
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveRow(null);
    // Reset AFTER the synthetic post-drag click has fired (it fires in the
    // same task right after pointerup; the timeout callback runs after it).
    window.setTimeout(() => {
      justDragged.current = false;
    }, 0);
    // Resolve the row from the event, not from drag-start state — immune to
    // stale-closure issues between dnd-kit's handler refs and React renders.
    const row = topRows.find((r) => r.id === event.active.id);
    // Fall back to the last-hovered lane when the drop-time collision is null
    // (auto-scroll race). overLaneRef is the lane the pointer was last over.
    const targetLaneId = (event.over?.id as string | undefined) ?? overLaneRef.current;
    overLaneRef.current = null;
    if (!row || !targetLaneId) return;
    const lane = lanes.find((l) => l.id === targetLaneId);
    if (!lane || lane.id === statusValueOf(row, statusColId)) return;
    if (!canDropOn(row, lane)) {
      showToast(t.kanbanDropNotAllowed, "error");
      return;
    }
    if (!isTickets) {
      if (onPatchCell && statusColId) onPatchCell(row.id, statusColId, lane.id);
      else onPatchRow(row.id, { status: lane.id });
      return;
    }
    const action = actionForTargetOption(lane.id, lane.role, viewerIsCreatorOf(row));
    if (!action) {
      showToast(t.kanbanDropNotAllowed, "error");
      return;
    }
    // Dropping on a specific closed lane fixes the outcome to that lane.
    const targetOptionId = lane.role === "closed" ? lane.id : undefined;
    setPendingDrop({ row, action, targetOptionId });
  }

  function submitPendingAction(payload: {
    body: string;
    recipient_user_ids: UUID[];
    target_option_id?: string;
    assignee_user_ids?: UUID[];
  }) {
    if (!pendingDrop) return;
    rowAction.mutate(
      { action: pendingDrop.action, ...payload },
      {
        onSuccess: () => setPendingDrop(null),
        onError: (e) => {
          const status = (e as { status?: number }).status;
          if (status === 422) showToast(t.actionStatusColumnMissing, "error");
          else showToast(t.actionSubmitFailed, "error");
        },
      },
    );
  }

  if (!statusCol || !statusCol.options || statusCol.options.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--ink-3)",
          fontSize: 14,
          fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        }}
      >
        {t.kanbanNoStatusColumn}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={laneUnderCursor}
      // Re-measure lane rects continuously during a drag — the default
      // (measure once at drag start) can serve stale rects on the first drag,
      // which makes `over` come back null and the drop silently no-op.
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        setActiveRow(null);
        justDragged.current = false;
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          gap: 12,
          padding: 14,
          overflowX: "auto",
          alignItems: "stretch",
        }}
      >
        {lanes.map((lane) => (
          <KanbanLane
            key={lane.id}
            lane={lane}
            rows={rowsByLane.get(lane.id) ?? []}
            isTickets={isTickets}
            dragDisabled={isIdeas}
            ticketPrefix={board.ticketPrefix}
            activeRow={activeRow}
            activeStatus={activeRow ? statusValueOf(activeRow, statusColId) : null}
            droppable={activeRow ? canDropOn(activeRow, lane) : false}
            onRowClick={handleCardClick}
            avatarUsersFor={avatarUsersFor}
            openRowId={openRowId}
          />
        ))}
      </div>

      <DragOverlay dropAnimation={null}>
        {activeRow ? (
          <KanbanCardShell
            row={activeRow}
            isTickets={isTickets}
            ticketPrefix={board.ticketPrefix}
            users={avatarUsersFor(activeRow)}
            dragging
          />
        ) : null}
      </DragOverlay>

      <ActionDialog
        open={pendingDrop !== null}
        onOpenChange={(o) => !o && setPendingDrop(null)}
        action={pendingDrop?.action ?? null}
        defaultRecipients={
          pendingDrop ? defaultRecipientsFor(pendingDrop.row, pendingDrop.action) : []
        }
        candidateMembers={recipientPool}
        busy={rowAction.isPending}
        onSubmit={submitPendingAction}
        outcomeOptions={closedOutcomes}
        defaultOutcomeId={pendingDrop?.targetOptionId ?? null}
        assigneeOptions={assigneeOptions}
        defaultAssigneeId={me?.user_id ?? null}
      />
    </DndContext>
  );
}

interface KanbanLaneProps {
  lane: Lane;
  rows: ViewRow[];
  isTickets: boolean;
  /** Ideas boards disable card pickup entirely (no drag-to-status). */
  dragDisabled: boolean;
  ticketPrefix: string | null;
  activeRow: ViewRow | null;
  activeStatus: string | null;
  openRowId?: string | null;
  droppable: boolean;
  onRowClick: (row: ViewRow) => void;
  avatarUsersFor: (row: ViewRow) => AvatarUser[];
}

function KanbanLane({
  lane,
  rows,
  isTickets,
  dragDisabled,
  ticketPrefix,
  activeRow,
  activeStatus,
  droppable,
  onRowClick,
  avatarUsersFor,
  openRowId,
}: KanbanLaneProps) {
  const { setNodeRef, isOver } = useDroppable({ id: lane.id });
  const tint = statusColorOf(lane.color);
  // While a card is being dragged, lanes it can't legally land in dim out (but
  // not the card's own current lane).
  const dimmed = activeRow !== null && !droppable && lane.id !== activeStatus;

  return (
    <div
      ref={setNodeRef}
      data-testid="kanban-lane"
      data-lane-id={lane.id}
      style={{
        width: 290,
        minWidth: 290,
        display: "flex",
        flexDirection: "column",
        borderRadius: 10,
        background: isOver && droppable ? "var(--surface-soft)" : "var(--bg)",
        border: `1px solid ${isOver && droppable ? tint.dot : "var(--hairline)"}`,
        opacity: dimmed ? 0.45 : 1,
        transition: "opacity .15s, border-color .15s, background .15s",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
          borderBottom: "1px solid var(--hairline-soft)",
        }}
      >
        <span
          style={{
            width: 9,
            height: 9,
            borderRadius: 999,
            background: tint.dot,
            flexShrink: 0,
          }}
        ></span>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-1)" }}>{lane.label}</span>
        <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{rows.length}</span>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 60,
          overflowY: "auto",
          padding: 8,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {rows.length === 0 ? (
          <div
            style={{
              padding: "18px 0",
              textAlign: "center",
              fontSize: 12,
              color: "var(--ink-4)",
            }}
          >
            {t.kanbanEmptyLane}
          </div>
        ) : (
          rows.map((row) => (
            <KanbanCard
              key={row.id}
              row={row}
              isTickets={isTickets}
              dragDisabled={dragDisabled}
              ticketPrefix={ticketPrefix}
              users={avatarUsersFor(row)}
              onClick={() => onRowClick(row)}
              isOpen={openRowId === row.id}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface KanbanCardProps {
  row: ViewRow;
  isTickets: boolean;
  /** Ideas boards disable card pickup entirely (no drag-to-status). */
  dragDisabled: boolean;
  ticketPrefix: string | null;
  users: AvatarUser[];
  onClick: () => void;
  isOpen?: boolean;
}

function KanbanCard({
  row,
  isTickets,
  dragDisabled,
  ticketPrefix,
  users,
  onClick,
  isOpen,
}: KanbanCardProps) {
  // `disabled` makes the listeners inert so an ideas card can't be picked up —
  // no "lifted card can't drop anywhere" dead-UX. onClick (row detail) is on
  // the wrapper div, independent of drag, so it keeps working.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: row.id,
    disabled: dragDisabled,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={onClick}
      data-testid="kanban-card"
      data-row-id={row.id}
      style={{
        opacity: isDragging ? 0.35 : 1,
        touchAction: dragDisabled ? undefined : "none",
        cursor: dragDisabled ? "pointer" : undefined,
      }}
    >
      <KanbanCardShell
        row={row}
        isTickets={isTickets}
        ticketPrefix={ticketPrefix}
        users={users}
        isOpen={isOpen}
        dragDisabled={dragDisabled}
      />
    </div>
  );
}

interface KanbanCardShellProps {
  row: ViewRow;
  isTickets: boolean;
  ticketPrefix: string | null;
  users: AvatarUser[];
  dragging?: boolean;
  isOpen?: boolean;
  // On ideas boards cards can't be dragged (status changes via the action bar),
  // so the shell shows the clickable pointer cursor instead of the grab handle.
  dragDisabled?: boolean;
}

function KanbanCardShell({
  row,
  isTickets,
  ticketPrefix,
  users,
  dragging = false,
  isOpen = false,
  dragDisabled = false,
}: KanbanCardShellProps) {
  const [dateFormat] = useDateFormat();
  // A completed row (checked task / resolved ticket) is never "überfällig".
  const due = relDate(
    row.due,
    undefined,
    row.done || !!row.raw?.resolved_at,
    undefined,
    dateFormat,
  );
  const idLabel = isTickets ? ticketLabel(ticketPrefix, row.ticketNumber) : null;

  return (
    <div
      style={{
        // Open-detail card: persistent brand-blue border + soft ring so a bell
        // deep-link makes clear which card the side panel belongs to.
        background: isOpen ? "rgba(0, 89, 155, 0.05)" : "var(--surface)",
        border: `1px solid ${isOpen ? BRAND.blue : "var(--hairline)"}`,
        borderRadius: 10,
        padding: "10px 12px",
        cursor: dragDisabled ? "pointer" : dragging ? "grabbing" : "grab",
        boxShadow: dragging
          ? "0 8px 24px rgba(13,62,99,0.18)"
          : isOpen
            ? `0 0 0 1px ${BRAND.blue}`
            : "0 1px 0 rgba(13,62,99,0.02)",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        userSelect: "none",
      }}
    >
      {idLabel ? (
        <div style={{ marginBottom: 4 }}>
          <TicketIdChip label={idLabel} />
        </div>
      ) : null}
      <div
        style={{
          fontSize: 13.5,
          fontWeight: 500,
          color: "var(--ink-1)",
          lineHeight: 1.35,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          marginBottom: 8,
        }}
      >
        {row.title || "—"}
      </div>

      {row.tags.length > 0 || due ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            marginBottom: 8,
          }}
        >
          {due ? (
            <span
              style={{
                fontSize: 11,
                padding: "2px 7px",
                borderRadius: 5,
                fontWeight: 500,
                background: due.overdue ? "#F5DDDB" : "var(--surface-soft)",
                color: due.overdue ? "var(--danger)" : "var(--ink-2)",
              }}
            >
              {due.label}
            </span>
          ) : null}
          {row.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              style={{
                fontSize: 11,
                padding: "2px 7px",
                borderRadius: 5,
                background: "var(--surface-soft)",
                color: "var(--ink-2)",
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      ) : null}

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {users.length > 0 ? <AvatarStack users={users} size={20} max={3} /> : null}
        <div style={{ flex: 1 }}></div>
        {row.commentCount > 0 ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              fontSize: 11.5,
              color: "var(--ink-3)",
            }}
          >
            <Icon name="dotsV" size={11} color="var(--ink-4)" />
            {row.commentCount}
          </span>
        ) : null}
        {isTickets ? (
          <TicketAgeChip createdAt={row.raw?.created_at} resolvedAt={row.raw?.resolved_at} />
        ) : null}
      </div>
    </div>
  );
}
